import React, { useState } from "react";
import { ShieldCheck, Search, XCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api from "../lib/api";

export default function OriginalityPage() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    setError("");
    try {
      const r = await api.get(`/autenticidade/${encodeURIComponent(code.trim())}`);
      setResult(r.data);
    } catch {
      setError("CÓDIGO NÃO RECONHECIDO — este código não consta em nosso sistema. Este produto pode ser uma FALSIFICAÇÃO. Verifique a digitação ou fale com o suporte.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="originality-page">
      <Header />
      <main className="max-w-2xl mx-auto px-6 py-24 min-h-[65vh]">
        <div className="text-center mb-12 animate-fade-up">
          <ShieldCheck size={44} className="text-gold mx-auto mb-6" />
          <p className="label-caps mb-3">Certificado ZEFERIUS</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium" data-testid="originality-title">Verificar autenticidade</h1>
          <p className="text-zinc-400 mt-4">Digite o código da compra para confirmar a originalidade e consultar a garantia.</p>
        </div>

        <form onSubmit={verify} className="flex gap-3" data-testid="originality-form">
          <Input
            data-testid="originality-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ZF-XXXX-XXXX"
            className="bg-[#141414] border-white/10 focus:border-amber-500 font-mono text-lg h-13 py-3.5 tracking-widest text-center"
          />
          <Button type="submit" disabled={loading} data-testid="originality-verify-button"
            className="bg-gold text-black hover:bg-gold-light font-semibold px-7 h-auto transition-colors">
            <Search size={16} className="mr-2" /> {loading ? "..." : "Verificar"}
          </Button>
        </form>

        {error && (
          <div className="mt-8 border border-red-900/50 rounded-lg p-6 flex items-center gap-4 bg-red-950/20 animate-fade-up" data-testid="originality-error">
            <XCircle size={22} className="text-red-400 shrink-0" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {result && (
          <div className="mt-8 border border-gold/30 rounded-lg p-8 bg-[#111111] gold-glow animate-fade-up" data-testid="originality-result">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck size={24} className="text-gold" />
              <div>
                <p className="text-gold font-semibold tracking-wide" data-testid="originality-status">PRODUTO ORIGINAL</p>
                <p className="text-xs text-zinc-500 font-mono">{result.code}</p>
              </div>
            </div>
            <dl className="space-y-4 text-sm">
              <div className="flex justify-between gap-4 border-b border-white/5 pb-3">
                <dt className="text-zinc-500">Registrado para</dt>
                <dd className="text-zinc-100 text-right" data-testid="originality-owner">{result.customer_name}</dd>
              </div>
              {result.cpf_last3 && (
                <div className="flex justify-between gap-4 border-b border-white/5 pb-3">
                  <dt className="text-zinc-500">CPF cadastrado</dt>
                  <dd className="text-zinc-100 font-mono" data-testid="originality-cpf">***.***.***-{result.cpf_last3}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4 border-b border-white/5 pb-3">
                <dt className="text-zinc-500">Pedido</dt>
                <dd className="text-zinc-100" data-testid="originality-order">#{String(result.order_number).padStart(4, "0")}</dd>
              </div>
              <div className="border-b border-white/5 pb-3">
                <dt className="text-zinc-500 mb-2">Itens</dt>
                {result.items.map((i, idx) => (
                  <dd key={idx} className="text-zinc-100 text-sm py-0.5">
                    {i.name} <span className="text-zinc-500">({i.variation_name})</span>
                    {(i.serials || []).length > 0 && (
                      <span className="text-gold/80 font-mono text-xs ml-2">Nº {i.serials.map((s) => String(s).padStart(4, "0")).join(", ")}</span>
                    )}
                  </dd>
                ))}
              </div>
              <div className="flex justify-between gap-4 border-b border-white/5 pb-3">
                <dt className="text-zinc-500">Compra confirmada em</dt>
                <dd className="text-zinc-100">{result.purchase_date ? new Date(result.purchase_date).toLocaleDateString("pt-BR") : "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-zinc-500">Garantia válida até</dt>
                <dd className="text-gold font-medium" data-testid="originality-warranty">
                  {result.warranty_until ? new Date(result.warranty_until).toLocaleDateString("pt-BR") : "—"}
                </dd>
              </div>
            </dl>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
